import { Component, forwardRef, Input, OnInit } from '@angular/core';
import { FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { DropdownModule } from 'primeng/dropdown';
import { BaseSelectComponent } from '../base-select/base-select.component';
import { CommonModule } from '@angular/common';
import { BreakpointObserver } from '@angular/cdk/layout';
import { InfoService } from 'src/app/shared/api-services/info.service';
import { takeUntil } from 'rxjs';

export interface CultureInfoOption {
  name: string;
  displayName: string;
}

@Component({
  selector: 'culture-info-select',
  imports: [
    TranslateModule,
    DropdownModule,
    CommonModule,
    FormsModule
  ],
  template: `
    <!-- PrimeNG Dropdown per desktop -->
    <p-dropdown
      *ngIf="!isMobile"
      class="w-100"
      [placeholder]="placeholder | translate"
      [(ngModel)]="selectedDropdownValue"
      [options]="listItems"
      optionLabel="name"
      [showClear]="true"
      [filter]="true"
      [filterBy]="'name'"
      (onFilter)="filterItems($event.filter)"
      (onChange)="handleDropdownChange($event.value)"
      (onShow)="onDropdownShow()"
      [ngClass]="{'has-value': hasValue, 'is-invalid': isInvalid}"
      [loading]="isLoading"
      [disabled]="disabled"
      [readonly]="isReadonly"
      [required]="isRequired"
      [dataKey]="'item.name'">
    </p-dropdown>

    <!-- Select nativo per mobile -->
    <select
      *ngIf="isMobile"
      class="form-control mobile-select w-100"
      [class.is-invalid]="isInvalid"
      [disabled]="disabled"
      (change)="onNativeSelectChange($event)"
      (focus)="onNativeSelectFocus()">
      <option value="">{{ placeholder | translate }}</option>
      <option *ngFor="let option of listItems" [value]="option.item.name" [selected]="selectedDropdownValue?.item?.name === option.item.name">
        {{ option.name }}
      </option>
    </select>
  `,
  styleUrls: ['../base-select/base-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CultureInfoSelectComponent),
      multi: true
    }
  ]
})
export class CultureInfoSelectComponent extends BaseSelectComponent<CultureInfoOption, void> implements OnInit {
  @Input() cultureInfoName: string | null = null;
  @Input() disabled: boolean = false;
  @Input() isReadonly: boolean = false;
  @Input() isRequired: boolean = false;
  @Input() isInvalid: boolean = false;

  selectedDropdownValue: { name: string, item: CultureInfoOption } | null = null;
  private allCultureInfos: CultureInfoOption[] = [];

  constructor(
    private _infoService: InfoService,
    breakpointObserver: BreakpointObserver
  ) {
    super(breakpointObserver);
    this.placeholder = 'CULTURE_INFOS';
  }

  override ngOnInit(): void {
    super.ngOnInit();
    
    if (this.cultureInfoName) {
      this.fetchAndSetInitialCultureInfo(this.cultureInfoName);
    } else {
      this.getItems('');
    }
  }

  fetchAndSetInitialCultureInfo(cultureInfoName: string): void {
    // Prima carica tutti i dati
    this.getItems('');
    
    const foundCultureInfo = this.allCultureInfos.find(ci => ci.name === cultureInfoName);
    
    if (foundCultureInfo) {
      this.writeValue(foundCultureInfo);
      
      const currentOptionInList = this.listItems.find(opt => opt.item.name === foundCultureInfo.name);
      if (!currentOptionInList) {
        this.listItems = [{ name: foundCultureInfo.displayName, item: foundCultureInfo }, ...this.listItems.filter(opt => opt.item.name !== foundCultureInfo.name)];
      }
    }
  }

  override writeValue(obj: string | CultureInfoOption): void {
    if (obj) {
      let cultureInfoToSet: CultureInfoOption | null = null;
      
      // Se obj è una stringa (valore dal FormControl)
      if (typeof obj === 'string') {
        cultureInfoToSet = this.allCultureInfos.find(ci => ci.name === obj) || null;
      } else {
        // Se obj è già un oggetto CultureInfoOption
        cultureInfoToSet = obj;
      }
      
      if (cultureInfoToSet) {
        // Verifica se il valore è già quello corrente per evitare loop infiniti
        const currentValue = this.selectedDropdownValue?.item?.name;
        if (currentValue !== cultureInfoToSet.name) {
          this.hasValue = true;
          this.selectedDropdownValue = { name: cultureInfoToSet.displayName, item: cultureInfoToSet };
          // Non chiamare onChange qui per evitare loop infiniti
          this.onSelectedItem.emit(cultureInfoToSet);
        }
      }
    } else {
      if (this.hasValue || this.selectedDropdownValue) {
        this.hasValue = false;
        this.selectedDropdownValue = null;
        this.onSelectedItem.emit(null as any);
      }
    }
  }

  override onSelectItem(item: CultureInfoOption): void {
    if (item) {
      const currentValue = this.selectedDropdownValue?.item?.name;
      if (currentValue !== item.name) {
        this.hasValue = true;
        this.selectedDropdownValue = { name: item.displayName, item: item };
        // Emetti solo il valore stringa al FormControl
        this.onChange(item.name);
        this.onSelectedItem.emit(item);
      }
    }
  }

  override onClearItem(): void {
    this.hasValue = false;
    this.selectedDropdownValue = null;
    this.onChange(null);
    this.onSelectedItem.emit(null as any);
  }

  handleDropdownChange(selectedOptionFromDropdown: { name: string, item: CultureInfoOption } | null): void {
    if (selectedOptionFromDropdown) {
      super.onSelectItem(selectedOptionFromDropdown.item);
    } else {
      super.onClearItem();
    }
  }

  filterItems(query: string): void {
    if (!query || query.trim() === '') {
      this.listItems = this.allCultureInfos.map((cultureInfo) => ({ 
        name: cultureInfo.displayName, 
        item: cultureInfo 
      }));
    } else {
      const filteredCultureInfos = this.allCultureInfos.filter(ci => 
        ci.displayName.toLowerCase().includes(query.toLowerCase()) ||
        ci.name.toLowerCase().includes(query.toLowerCase())
      );
      this.listItems = filteredCultureInfos.map((cultureInfo) => ({ 
        name: cultureInfo.displayName, 
        item: cultureInfo 
      }));
    }
  }

  override getItems(query: string): void {
    // Se abbiamo già i dati, filtriamo localmente
    if (this.allCultureInfos.length > 0) {
      this.filterItems(query);
      return;
    }

    this.isLoading = true;
    
    this._infoService.getCultureInfos$()
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (response) => {
          this.allCultureInfos = response.map(ci => ({
            name: ci.name,
            displayName: ci.displayName
          }));
          
          this.filterItems(query);
          
          // Se abbiamo un culture info da impostare inizialmente, fallo ora
          if (this.cultureInfoName && !this.hasValue) {
            this.fetchAndSetInitialCultureInfo(this.cultureInfoName);
          }
          
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
        }
      });
  }
} 